import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Pause, Play, Search, Square, Star } from "lucide-react";
import { FeaturedProCard } from "@/components/featured/FeaturedProCard";

const PLACEMENT_LABELS = {
  HOME_FEATURED_PROFESSIONALS: "Homepage carousel",
  CONNECTIONS_PROFESSIONAL_SPOTLIGHT: "Connections · Discover",
  MARKETPLACE_FEATURED_PROFESSIONALS: "Marketplace (contextual)",
};
const STATUS_STYLE = {
  LIVE: "bg-emerald-100 text-emerald-700", UPCOMING: "bg-sky-100 text-sky-700", PAUSED: "bg-amber-100 text-amber-800",
  EXPIRED: "bg-slate-200 text-slate-600", CANCELLED: "bg-slate-100 text-slate-500", INELIGIBLE: "bg-rose-100 text-rose-700",
};
const METRICS = [["impression", "Impressions"], ["click", "Profile clicks"], ["profile_view", "Profile views"],
  ["inquiry_click", "Inquiry clicks"], ["inquiry_submitted", "Inquiries"], ["connection_request", "Connection requests"]];
const manilaToday = () => new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 10);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Manila" }) : "—");

function Totals({ t }) {
  if (!t) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9" data-testid="fp-admin-totals">
      {METRICS.map(([k, l]) => (
        <div key={k} className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-[11px] text-slate-500">{l}</div><div className="font-display text-lg font-bold text-slate-900" data-testid={`fp-total-${k}`}>{t[k]}</div></div>
      ))}
      <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-[11px] text-slate-500">CTR</div><div className="font-display text-lg font-bold">{t.ctr}%</div></div>
      <div className="rounded-xl border border-slate-200 bg-white p-3" title="Submitted inquiries ÷ impressions (clicks excluded)"><div className="text-[11px] text-slate-500">Inquiry conversion</div><div className="font-display text-lg font-bold" data-testid="fp-total-inquiry-conversion">{t.inquiry_conversion_rate}%</div></div>
      <div className="rounded-xl border border-slate-200 bg-white p-3" title="(Submitted inquiries + connection requests) ÷ impressions"><div className="text-[11px] text-slate-500">Total conversion</div><div className="font-display text-lg font-bold">{t.conversion_rate}%</div></div>
    </div>
  );
}

function CandidatePicker({ selected, onSelect }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/featured-professionals/candidates", { params: q ? { q } : {} })
      .then((r) => setRows(r.data || [])).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="fp-candidate-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search published professionals…" className="pl-9" /></div>
      <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-3 text-xs text-slate-500">No eligible professionals (active + published profile required).</div>}
        {rows.map((r) => (
          <button key={r.id} type="button" data-testid={`fp-candidate-${r.id}`} onClick={() => onSelect(r)}
            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${selected?.id === r.id ? "bg-[#C79A3B]/10" : ""}`}>
            <span className="truncate">{r.name} <span className="text-xs text-slate-400">/{r.public_slug}{r.city ? ` · ${r.city}` : ""}</span></span>
            {r.verified && <span className="text-[10px] font-semibold text-[#1D63D8]">Verified</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function CreateForm({ placements, onCreated }) {
  const [pro, setPro] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState(placements);
  const [start, setStart] = useState(manilaToday());
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!pro) { setPreview(null); return; }
    api.get(`/admin/featured-professionals/preview/${pro.id}`).then((r) => setPreview(r.data)).catch((e) => toast.error(apiError(e)));
  }, [pro]);
  const toggle = (p) => setSel((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/featured-professionals", { professional_id: pro.id, placements: sel, start_date: start, duration_days: Number(days) });
      toast.success(`${pro.name} is now featured`); setPro(null); onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-6 rounded-2xl border border-slate-200 bg-white p-5 lg:grid-cols-[1fr_280px]" data-testid="fp-create-form">
      <div className="space-y-4">
        <h3 className="font-display font-bold text-slate-900">Feature a professional</h3>
        <CandidatePicker selected={pro} onSelect={setPro} />
        <div>
          <div className="mb-1.5 text-xs font-semibold text-slate-600">Placements</div>
          <div className="flex flex-wrap gap-2">
            {placements.map((p) => (
              <label key={p} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
                <input type="checkbox" data-testid={`fp-placement-${p}`} checked={sel.includes(p)} onChange={() => toggle(p)} /> {PLACEMENT_LABELS[p] || p}
              </label>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="text-xs font-semibold text-slate-600">Start date (Manila)<Input type="date" data-testid="fp-start-date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-44" /></label>
          <label className="text-xs font-semibold text-slate-600">Duration (days)<Input type="number" min={1} max={366} data-testid="fp-duration" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 w-28" /></label>
        </div>
        <Button data-testid="fp-create-submit" disabled={!pro || !sel.length || busy} onClick={submit} className="bg-[#0F2C59] hover:bg-[#143163]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Star className="mr-1.5 h-4 w-4" /> Feature professional</>}
        </Button>
        <p className="text-[11px] text-slate-500">Admin-managed placement. No payment is collected; pricing packages stay inactive.</p>
      </div>
      <div data-testid="fp-preview">
        <div className="mb-2 text-xs font-semibold text-slate-600">Card preview</div>
        {preview ? <FeaturedProCard c={{ id: "", entity: preview }} placement="" /> : <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">Select a professional to preview their featured card.</div>}
      </div>
    </div>
  );
}

function PlacementRow({ it, act }) {
  const s = it.display_status;
  const canPause = ["SCHEDULED", "ACTIVE"].includes(it.lifecycle_state) && s !== "EXPIRED";
  const canExpire = ["APPROVED", "SCHEDULED", "ACTIVE", "SYSTEM_PAUSED"].includes(it.lifecycle_state) && s !== "EXPIRED";
  const m = it.metrics;
  return (
    <tr className="border-b border-slate-100 align-top" data-testid={`fp-row-${it.id}`}>
      <td className="px-3 py-2"><div className="font-semibold text-slate-800">{it.professional.name}</div><div className="text-[11px] text-slate-400">/{it.professional.public_slug}{it.professional.verified ? " · Verified" : ""}</div></td>
      <td className="px-3 py-2 text-xs">{PLACEMENT_LABELS[it.placement] || it.placement}</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{fmt(it.starts_at)} → {fmt(it.ends_at)}</td>
      <td className="px-3 py-2"><span data-testid={`fp-status-${it.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[s] || "bg-slate-100"}`}>{s}</span></td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{m.impression} imp · {m.click} clicks · {m.ctr}% CTR<br />{m.profile_view} views · {m.inquiry_click} inq. clicks · <span className="font-semibold text-slate-800">{m.inquiry_submitted} inquiries ({m.inquiry_conversion_rate}%)</span> · {m.connection_request} conn.</td>
      <td className="px-3 py-2"><div className="flex gap-1.5">
        {canPause && <Button size="sm" variant="outline" data-testid={`fp-pause-${it.id}`} onClick={() => act(it.id, "pause")}><Pause className="h-3.5 w-3.5" /></Button>}
        {it.lifecycle_state === "SYSTEM_PAUSED" && <Button size="sm" variant="outline" data-testid={`fp-resume-${it.id}`} onClick={() => act(it.id, "resume")}><Play className="h-3.5 w-3.5" /></Button>}
        {canExpire && <Button size="sm" variant="outline" data-testid={`fp-expire-${it.id}`} onClick={() => act(it.id, "expire")} className="text-rose-600"><Square className="h-3.5 w-3.5" /></Button>}
      </div></td>
    </tr>
  );
}

export default function FeaturedProfessionalsAdmin() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/featured-professionals").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (cid, action) => {
    if (action === "expire" && !window.confirm("End this featured placement now? The professional's profile is unaffected.")) return;
    try {
      await api.post(action === "expire" ? `/admin/featured-professionals/${cid}/expire` : `/admin/campaigns/${cid}/${action}`);
      toast.success(`Placement ${action === "expire" ? "expired" : action === "pause" ? "paused" : "resumed"}`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-6" data-testid="fp-admin">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-slate-800">Featured Professionals</span>
        <span data-testid="fp-flag" className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${data.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>Delivery {data.enabled ? "ON" : "OFF"}</span>
      </div>
      <Totals t={data.totals} />
      <CreateForm placements={data.placements} onCreated={load} />
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="fp-table">
          <thead><tr className="border-b border-slate-200 text-left text-[11px] uppercase text-slate-400">
            <th className="px-3 py-2">Professional</th><th className="px-3 py-2">Placement</th><th className="px-3 py-2">Window</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Performance</th><th className="px-3 py-2">Actions</th></tr></thead>
          <tbody>{data.items.map((it) => <PlacementRow key={it.id} it={it} act={act} />)}</tbody>
        </table>
        {!data.items.length && <div className="p-6 text-center text-sm text-slate-500">No featured placements yet.</div>}
      </div>
    </div>
  );
}
